import { Container, ContainerPadding, Text, TokenTextAppearance } from '@volkswagen-onehub/components-core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { oneformBFFNdpsSend } from 'src/bff';
import { ConsentQuestion, GenericErrorLayer, NombreApellidos, TelefonoEmail } from 'src/components';
import { FormController, getTrackingForm, LayoutWrapper, removeWhiteSpace, useIsComerciales, useOneFormContext, useTrackingManager } from 'src/feature-app';
import { getParamsForm } from 'src/feature-app/get-params-form';
import { FormDataGroup, FormInfo, IdPerson, OneFormState, Steps } from 'src/types';
import { formDataBuilder } from '../format-fields';
import { ConfirmacionLeadsNoQuestions } from '../Leads';
import { ThankYouAlta } from './ThankYouAlta';

interface AltaComunicacionesProps {
	idPerson?: IdPerson;
}

export const formDataAlta: Partial<FormDataGroup> = {
		headers: {
			DPS_TOKEN: 'VW_DDB_FRM_ALTA_COMUNICACIONES',
			FORM_OBJECTIVE: 'Alta Comunicaciones',
			FORM_NAME: 'vw-oneform-alta_comunicaciones',
		},
		fields: {
			formName: 'alta_comunicaciones',
			formType: 'otherform',
			pageCategory: 'Alta Comunicaciones',
		}, // Analítica
	};

export const AltaComunicacionesForm = (props: AltaComunicacionesProps) => {
	const { initializeForm } = useOneFormContext();
	const { idPerson } = props;
	const store = useStore();
	const dispatch = useDispatch();
	const trackingManager = useTrackingManager();
	const isComerciales = useIsComerciales();
	const [error, setError] = useState(false);

	let index = 0;
	const steps: Steps[] = [
		{
			title: (
				<>
					<Text>
						Para poder contactar contigo, necesitamos saber <Text bold>quién eres</Text>
					</Text>
					<Container padding={{ top: ContainerPadding.static200 }}>
						<Text appearance={TokenTextAppearance.copy200}>Datos de contacto y preferencias para poder dar respuesta a tu solicitud de información</Text>
					</Container>
				</>
			),
			fields: (
				<LayoutWrapper>
					<NombreApellidos />
				</LayoutWrapper>
			),
			screenIndex: index++,
			name: 'NombreApellido',
			outputs: ['name', 'surname', 'secondSurname'],
			outputsText: ['name', 'surname', 'secondSurname'],
		},
		{
			title: (
				<>
					Para ello necesitaremos <Text bold>tus datos de contacto</Text>
				</>
			),
			fields: (
				<LayoutWrapper>
					<TelefonoEmail hidePrefijo={isComerciales} requiredTelefono requiredEmail />
				</LayoutWrapper>
			),
			screenIndex: index++,
			name: 'EmailTelefono',
			outputs: ['cellphone', 'email'],
			outputsText: ['cellphone', '/', 'email'],
		},
		{
			fields: <ConsentQuestion questionType="lssi" />,
			screenIndex: index++,
			name: 'lssi',
			outputs: ['lssi'],
		},
		{
			fields: <ConsentQuestion questionType="tmk" />,
			screenIndex: index++,
			name: 'tmk',
			outputs: ['tmk'],
		},
		{
			fields: <ConfirmacionLeadsNoQuestions />,
			screenIndex: index++,
			isLastStep: true,
			hidePreviousStep: true,
			name: 'Confirmacion',
		},
	];

	const getFormInfo = (): {
		firstStep: Steps;
		lastStep: Steps;
		stepsHistory: number[];
		numberOfScreens: number;
		newSteps?: Steps[];
	} => {
		return {
			firstStep: steps[0],
			lastStep: steps[steps.length - 1],
			stepsHistory: [0],
			numberOfScreens: steps.length,
			newSteps: null,
		};
	};

	const sendForm = (): Promise<boolean> => {
		const { formData }: OneFormState = store.getState();
		const { fields } = formData;

		const personEmail = idPerson && idPerson.PersonEmail ? idPerson.PersonEmail : removeWhiteSpace(fields.email);

		const personPhoneMobile = idPerson && idPerson.PersonMobilePhone ? idPerson.PersonMobilePhone : removeWhiteSpace(fields.cellphone);

		return new Promise<any>(async (resolve, reject) => {
			const { tc, today, url, referalUrl, device } = getParamsForm();

			const data = {
				'headers[DPS_TOKEN]': formDataAlta.headers.DPS_TOKEN,
				'headers[FORM_OBJECTIVE]': formDataAlta.headers.FORM_OBJECTIVE,
				'headers[FORM_NAME]': formDataAlta.headers.FORM_NAME,
				'headers[FORM_VERSION]': `${formDataAlta.headers.FORM_NAME}-${process.env.VERSION}`,
				'headers[FORM_REFERER]': url,
				'headers[FORM_DATE_INSERTION]': today,
				'headers[ORIGIN_TRACKING_CODE]': tc,
				'headers[ORIGIN_DEVICE_TYPE]': device,
				'headers[FK_SYSTEM]': idPerson && idPerson.RCY_ExternalAccountID__c ? idPerson.RCY_ExternalAccountID__c : '',
				'headers[SF_CAMPAIGN_NAME]': 'Call Center_Alta Comunicaciones',
				'headers[FORM_MKT_CHANNEL]': 'Call Center',

				'fields[DPS_BRAND]': isComerciales ? 'Volkswagen CV' : 'Volkswagen',
				'fields[PERSON_NAME]': idPerson && idPerson.FirstName ? idPerson.FirstName : fields.name,
				'fields[PERSON_SURNAME_1]': idPerson && idPerson.LastName ? idPerson.LastName : fields.surname,
				'fields[PERSON_SURNAME_2]': idPerson && idPerson.MiddleName ? idPerson.MiddleName : fields.secondSurname,
				'fields[PERSON_EMAIL]': personEmail,
				'fields[PERSON_PHONE_MOBILE]': personPhoneMobile,
				'fields[PERSON_RGPD_COM_COMERCIALES]': fields.lssi ? fields.lssi : 'NO INFORMADO',
				'fields[PERSON_RGPD_PERFILADO]': fields.tmk ? fields.tmk : 'NO INFORMADO',

				'flags[PERSON_RGPD_ADAPTED]': 'true',
				'flags[FLAG_RELACION_MARCA_SIN_IC]': 'true',
			};
			const dataStringified = JSON.stringify(data);
			const dataParsed = JSON.parse(dataStringified);
			const formData = formDataBuilder(dataParsed);
			const response = await oneformBFFNdpsSend(formData);

			if (response?.data?.status?.code == 200) {
				dispatch({ type: 'SET_FORM_RESPONSE', payload: response.data });

				trackingManager.trackFormSubmissionSuccessMessageLoad(
					{
						contentId: undefined,
					},
					getTrackingForm(response.data),
				);
				setError(false);
				resolve(true);
			} else {
				setError(true);
				resolve(false);
			}
		});
	};

	useEffect(() => {
		const { firstStep, lastStep, stepsHistory, numberOfScreens, newSteps } = getFormInfo();

		const NuevoFormInfo: Partial<FormInfo> = {
			formStarted: true,
			sendFormCallback: sendForm,
			fullScreen: firstStep.fullScreen ? true : false,
			lastStep,
			numberOfScreens,
			formTheme: 'main',
			screenIndex: firstStep.screenIndex,
			step: firstStep,
			steps: newSteps ? newSteps : steps,
			stepsHistory,
			notALayer: true,
			formType: 'inPage',
			alingItemsCenter: true,
			thankYouComponent: <ThankYouAlta />,
		};
		initializeForm(NuevoFormInfo, formDataAlta);
	}, []);

	return error ? <GenericErrorLayer title="Uy, algo ha pasado" bodycopy="Por alguna razón no hemos podido realizar la petición." notShowCTA={true} icon={false} notALayer={true} /> : <FormController steps={steps} screenType="full-screen" />;
};
